<template>
  <div>
    <template v-if="debug">
      <button class="btn btn-primary" @click.prevent="openOCRModal">Recognition modal</button>
      <button class="mr-4 btn btn-outline-primary" @click.prevent="openWFModal(0)">Open WF modal</button>
    </template>

    <div class="form-field card-number-field pt-3 pb-1 multiple-codes">
      <div class="position-relative">
        <template v-for="(code, idx) in model">
          <div v-if="code" :key="code.rid" class="card-number-field mb-3">
            <div class="d-flex flex-row align-center">
              <div class="position-relative overflow-hidden flex-grow-1">
                <input
                  type="hidden"
                  :name="`${codeInputName}[]`"
                  :value="code.value"
                />

                <input
                  type="hidden"
                  :name="`${tagInputName}[]`"
                  :value="tags[code.value] || ''"
                />

                <input :placeholder="codePlaceholder"
                       autocomplete="off"
                       :class="`serial-number my-0 ${className(code.status)} ${loading ? 'input_loading': ''}`"
                       type="text"
                       v-model="code.value"
                       :disabled="loading || (isGenericErrorExists() ? false : (code.status === Statuses.OK))"
                       @keydown.enter.prevent="(event) => submit(event, idx)"
                       data-1p-ignore
                       ref="serial-number"
                />

                <template v-if="!code.value && isOcrEnabled">
                  <div class="position-absolute btn-upload" @click="openOCRModal">
                    <span :class="`btn-upload__icon ${loading ? 'btn-upload__icon_disabled text-muted' : ''}`">
                      <i class="fa fa-camera text-black-20"/>
                    </span>
                  </div>
                </template>

                <div v-else class="action d-flex">
                  <template v-if="code.status === Statuses.PREFILLED || code.value !== code.oldValue">
                    <button class="btn btn-sm btn-link" @click.prevent="(event) => submit(event, idx)">{{saveButton}}</button>
                  </template>
                  <template v-else-if="code.status === Statuses.OK">
                    <div class="px-3 mt-2 pt-1 text-black-20">
                      <i class="fa fa-check-circle position-relative scaled-1.5 " aria-hidden="true"/>
                    </div>
                  </template>
                  <!--template v-if="status === Statuses.WARNING">
                    <div style="color: #F5B40B" class="pt-2 px-3">⚠︎︎</div>
                  </template>
                  <template v-if="status === Statuses.ERROR">
                    <div style="color: #d77" class="pt-2 px-3">✕</div>
                  </template-->
                </div>
              </div>

              <template v-if="isSumBased">
                <div class="ml-1 mt-1 pt-2 pl-1 pl-md-2">
                  <button
                    :disabled="(model.length <= 1) || !code.oldValue || loading"
                    class="btn btn-sm"
                    :title="removeHint"
                    @click.prevent="remove(code.rid)">
                    <i class="x-icon text-danger"></i>
                  </button>
                </div>
              </template>
            </div>

            <div v-if="code.message"
                 :class="`form-field-notify form-field-${code.status.toLowerCase()}`"
                 v-html="code.message"
                 @click="handleClockOnMessage"
            />
          </div>
        </template>
        <LoadingOverlay v-if="loading" />
      </div>

      <template v-for="code in recognized">
        <input type="hidden" :name="`${recognizedInputName}[]`" :value="code" />
      </template>

      <div :class="`row justify-md-content-between ${isSumBased ? 'mt-n2' : ''}`">
        <div class="col-12 col-md-7">
          <span
            class="form-label cursor-pointer"
            role="button"
            :aria-label="labelHint"
            onclick="$('.card-bonus-popup').addClass('opened'); return false;" v-text="labelHint"/>
        </div>
        <template v-if="isSumBased">
          <div class="col-12 col-md-5 pt-3 pt-md-0">
            <codes-progress
              class="mr-4 pr-1"
              v-model="scoreValue"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { uniq, unionBy, intersectionBy, filter } from "lodash";

import { Statuses } from "../../ocrComponent/Statuses.model";
import { checkElementIsAvailable } from "../../../utils/domChecker";
import chooseTypeModal from "./chooseTypeModal.vue";
import LoadingOverlay from "../../muplipleSerialOCR/LoadingOverlay.vue";
import CodesProgress from "../../muplipleSerialOCR/Progress.vue";

export default {
  name: 'WorkflowCodes',
  components: {
    CodesProgress, LoadingOverlay,
    chooseTypeModal,
  },
  props: {
    validateEndpoint: { type: String, required: true },
    workflowTypeDetectionEndpoint: { type: String, required: true },

    labelHint: { type: String, required: true },
    removeHint: { type: String, required: false },

    codePlaceholder: { type: String, required: false },
    saveButton: { type: String, required: true },
    uniqueCodesError: { type: String, required: false },
    emptyCodeError: { type: String, required: false },
    verifyCodesError: { type: String, required: false },

    codeInputName: { type: String, required: true },
    tagInputName: { type: String, required: true },
    recognizedInputName: { type: String, required: true },
    codes: { type: [String, Array, Object], required: false }, // Accepts JSON string or object/array
    score: { type: [String, Number], required: false },
    promoSettings: {type: [String, Boolean, Object], required: false },
    propRecognized: { type: Array, required: false, default: () => ([]) } // Use correct type if known
  },
  data() {
    return {
      debug: false,

      options: JSON.parse(this.promoSettings),
      loading: false,

      list: JSON.parse(this.codes || '[]'),
      model: [],
      idx_wip: null, // idx of the code in progress to specify what the workflow is

      tags: {},

      scoreValue: Number(this.score),
      recognized: this.propRecognized,

      partnerSelectorEnabled: false,
    };
  },
  mounted() {
    this.model = this.list
      .map((item) => ({
        ...item,
        oldValue: item.value,
        rid: this.rid(),
      }));

    this.list.forEach((item) => {
      this.tags[item.value] = item.tag;
    })

    this.$nextTick(() => {
      this.updateSubmitFormStatus();
    });

    this.$nextTick(() => {
      if (this.sumBasedNeedAnotherField() || !this.model.length) {
        this.addNewField();
      }
    });

    this.partnerSelectorEnabled = !!$('.partner-select').length;

    if (this.partnerSelectorEnabled) {
      this.partnerId = $('.partner-id').val();
      this.partnerInputName = $('.partner-id').attr('name');

      $('.partner-select .option').on('click', () => {
        this.$nextTick(() => {
          this.partnerId = $('.partner-id').val();
        })
      })
    }

    this.$eventBus.$on('codes:add', this.addOCRCodes);
    this.$eventBus.$on('codes:update', this.updateCodeWorkflow);
    this.$eventBus.$on('focus:serial-number', this.focusOnInput);
  },

  unmounted() {
    if (this.partnerSelectorEnabled) {
      $('.partner-select .option').off('click');
    }
  },

  beforeDestroy() {
    this.$eventBus.$off('codes:add');
    this.$eventBus.$off('codes:update');
    this.$eventBus.$off('focus:serial-number');
  },

  computed: {
    Statuses() {
      return Statuses
    },
    isSumBased() {
      return this.options.sumBased;
    },
    isOcrEnabled() {
      return this.options.ocr;
    },

    className: () => (status) => {
      let res = '';

      const map = {
        [Statuses.ERROR]: 'error',
        [Statuses.WARNING]: 'warning',
        [Statuses.OK]: 'muted',
      };

      if (Object.keys(map).includes(status)) {
        res = `input-${map[status]}`
      }

      return res;
    },

    headers() {
      return {
        "X-CSRF-Token":
        document.querySelector("meta[name='csrf-token']")?.content
      };
    },
  },

  methods: {
    handleClockOnMessage(e) {
      e.preventDefault();
      e.stopPropagation();

      const { target } = e;

      if (target.classList.contains('dynamic_pin_hint')) {
        $('.card-bonus-popup').addClass('opened');
      }
    },

    isGenericErrorExists() {
      return !!document.querySelector('.form-error', '.form-warning');
    },

    sumBasedNeedAnotherField() {
      return this.isSumBased && this.scoreValue && this.model.length && this.model.every(c => c.status === Statuses.OK);
    },

    rid() {
      return `input_${Math.random().toString(32).substring(2)}`;
    },

    openOCRModal() {
      this.$eventBus.$emit('open:upload-modal', {
        isSumBased: this.isSumBased
      });
    },

    openWFModal(idx) {
      this.idx_wip = idx;

      this.$eventBus.$emit('open:choose-workflow-modal', {
        value: this.model[idx],
      });
    },

    addOCRCodes(codes) {
      // update recognized list
      this.recognized = uniq([...this.recognized, ...codes.map(code => code.value)]);

      // check if the unique codes only
      const intersection = intersectionBy(this.model, codes, 'value');

      if (intersection.length) {
        alert(this.uniqueCodesError);
      }

      // update existing model
      this.model = unionBy(filter(this.model, (code => code.value)), codes, 'value')
        .map(code => ({
          ...code,
          rid: this.rid(),
        }));

      // update tags
      codes.forEach((code) => {
        this.tags[code.value] = code.tag;
      });

      // verify all codes
      this.verifyCodes(this.model.map(code => code.value));

      this.$nextTick(() => {
        this.$eventBus.$emit('close:upload-modal')
      })
    },

    updateCodeWorkflow(codes) {
      const [code] = codes;
      this.tags[code.value] = code.tag;

      this.idx_wip = null;

      this.$nextTick(() => {
        const codes = this.model.map(code => code.value).filter((code) => !!code);
        const uniques = uniq(codes);

        this.verifyCodes(uniques);
      });
    },

    submit(event, index) {
      event.preventDefault();
      event.stopPropagation();

      const codes = this.model.map(code => code.value).filter((code) => !!code);
      const uniques = uniq(codes);

      if (!codes.length) {
        alert(this.emptyCodeError);
        return;
      } else if (codes.length > uniques.length) {
        alert(this.uniqueCodesError);
        return;
      }

      const code = this.model[index].value;

      this.defineWorkflow([code])
        .then((response) => {
          const value = response[code];

          if (value !== 'NOT_SPECIFIED') {
            this.tags[code] = value;
            this.verifyCodes(uniques);
          } else {
            this.openWFModal(index);
          }
        });
    },

    remove(rid) {
      const idx = this.model.findIndex(item => item.rid === rid);
      const value = this.model[idx].value;

      this.recognized = this.recognized.filter(item => item !== value);
      this.model.splice(idx, 1);
      const codes = this.model.map(code => code.value).filter((code) => !!code);
      if (codes.length) {
        this.verifyCodes(codes);
      } else {
        this.scoreValue = 100;
      }

      this.addNewField();
    },

    addNewField() {
      const emptyField = this.model.find(item => !item.value);
      if (!this.scoreValue || emptyField) {
        this.focusOnInput();
      } else {
        this.model.push({
          value: '',
          rid: this.rid(),
        });

        this.focusOnInput();
      }
    },

    focusOnInput() {
      setTimeout(() => {
        const $input = this.$refs['serial-number'][this.model.length - 1];
        $input.focus();
      }, 100);
    },

    updateModelWithNewCodes(codes) {
      this.model = this.model.map(oldCode => {
        const newCodeObject = codes.find(code => code.value === oldCode.value);
        if (newCodeObject) {
          return {
            ...newCodeObject,
            oldValue: newCodeObject.value,
            rid: this.rid(),
          }
        } else {
          return oldCode;
        }
      });
    },

    verifyCodes(codes) {
      let recaptchaFlowIsStated = false;
      this.loading = true;
      const recaptchaResponsePromise = window.getRecaptchaResponse ? window.getRecaptchaResponse() : Promise.resolve();

      // Start a ticker to monitor the CAPTCHA modal state since there is NO specific event to listen to dismiss it
      const captchaTicker = setInterval(() => {
        if (!checkElementIsAvailable('[title*=recaptcha]') && this.loading && !recaptchaFlowIsStated) {
          clearInterval(captchaTicker);
          this.loading = false;
        }
      }, 600);

      return recaptchaResponsePromise.then(response => {
        recaptchaFlowIsStated = true;
        const data = new FormData();

        data.append('g-recaptcha-response', response);
        for (const code of codes) {
          if (code && this.tags[code]) {
            data.append(`${this.codeInputName}[]`, code);
            data.append(`${this.tagInputName}[]`, this.tags[code] || '');
          }
        }

        if (this.partnerSelectorEnabled) {
          data.append(this.partnerInputName, this.partnerId);
        }

        return axios({
          method: "post",
          url: this.validateEndpoint,
          headers: this.headers,
          data,
        })
      }).then(res => {
        const {codes, score} = res.data;

        this.scoreValue = score;
        this.updateModelWithNewCodes(codes);
      }).then(() => {
        if (this.sumBasedNeedAnotherField()) {
          this.addNewField();
        }
      }).then(() => {
        this.updateSubmitFormStatus();
      }).catch(() => {
        alert(this.verifyCodesError);
      }).finally(() => {
        recaptchaFlowIsStated = false;
        this.loading = false;
        clearInterval(captchaTicker);
      })
    },

    defineWorkflow(codes) {
      this.loading = true;

      return axios({
          method: "post",
          url: this.workflowTypeDetectionEndpoint,
          data: {codes},
          headers: this.headers
        }).then((response) => {
          return response.data;
        })
        .catch(e => {
          console.error(e);
        })
        .finally(() => {
          this.loading = false;
        })
    },

    updateSubmitFormStatus() {
      const values = this.model.map(code => code.value).filter((code) => !!code);
      let formIsValid = !this.scoreValue && values.length && this.model.every(code => code.status === Statuses.OK);

      this.$eventBus.$emit('wizard_step:toggle', 3, formIsValid);
    }
  },
};

</script>
